import { createApi, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { buildBaseQuery } from '@app/api';

export interface ChatStateOption {
  id: string;
  label: string;
  subtitle?: string;
}

export interface ChatStateOptions {
  courses?: ChatStateOption[];
  members?: ChatStateOption[];
  teeTimePresets?: ChatStateOption[];
  availabilityPresets?: number[];
  availabilityWindowPresets?: ChatStateOption[];
}

export interface ChatResponse {
  reply: string;
  conversationId: string;
  state?: {
    date?: string;
    resolvedDate?: string;
    displayDate?: string;
    time?: string;
    resolvedTimeEt?: string;
    displayTimeEt?: string;
    availability?: number;
    availabilityMinutes?: number;
    phase?: string;
    options?: ChatStateOptions;
    courses?: Array<{ id: string; priority: number; name?: string }>;
    selectedCourseNames?: string[];
    selectedMemberNames?: string[];
    selectedGuestNames?: string[];
    scheduleNotice?: string;
    groups?: Array<{
      index?: number;
      members?: string[];
      guests?: string[];
    }>;
    comment?: string;
  } | null;
  ticket?: unknown;
  error?: string;
}

export interface ChatRequest {
  message: string;
  conversationId?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function parseFetchChatError(error: FetchBaseQueryError | unknown): string {
  if (!isRecord(error)) {
    return 'Request failed';
  }

  const { status, data, error: errField } = error;

  if (isRecord(data)) {
    const { errors, message } = data;

    if (typeof errors === 'string') {
      return errors;
    }

    if (isRecord(errors) && typeof errors.message === 'string') {
      return errors.message;
    }

    if (typeof message === 'string') {
      return message;
    }
  }

  if (typeof errField === 'string' && errField.trim()) {
    return errField;
  }

  if (status === 'FETCH_ERROR' || status === 'PARSING_ERROR') {
    return 'Request failed';
  }

  if (typeof error.message === 'string') {
    return error.message;
  }

  return 'Request failed';
}

const CHAT_TIMEOUT_MS = 60000;

export const chatApi = createApi({
  reducerPath: 'chatApi',
  baseQuery: buildBaseQuery('', { timeout: CHAT_TIMEOUT_MS }),
  endpoints: (builder) => ({
    postChat: builder.mutation<ChatResponse, ChatRequest>({
      query: (body) => ({
        url: '/chat',
        method: 'POST',
        body,
      }),
      transformResponse: (raw: unknown): ChatResponse => {
        if (isRecord(raw) && raw.data !== undefined && raw.data !== null) {
          return raw.data as ChatResponse;
        }

        return raw as ChatResponse;
      },
    }),
  }),
});

export const { usePostChatMutation } = chatApi;
